import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Grid,
  Box,
  Chip,
  Button,
  CircularProgress,
  Alert,
  IconButton
} from '@mui/material';
import {
  ArrowBack,
  CheckCircle,
  Schedule,
  HourglassEmpty,
  Cancel,
  Visibility,
  Build,
  Star,
  ArrowBackIos,
  ArrowForwardIos,
  CheckOutlined
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { getService, resolveImageUrl } from '../utils/api';

const ImageSlider = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%', // Default fallback
  [theme.breakpoints.up('sm')]: {
    width: '70%',
  },
  height: 300,
  borderRadius: theme.spacing(1),
  overflow: 'hidden',
  marginBottom: theme.spacing(2),
}));

const SliderImage = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  display: 'block',
});

const SliderButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  color: 'white',
  zIndex: 1,
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
  },
}));

const SliderIndicators = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: theme.spacing(1),
  left: '50%',
  transform: 'translateX(-50%)',
  display: 'flex',
  gap: theme.spacing(0.5),
  zIndex: 1,
}));

const SliderDot = styled(Box)(({ theme, active }) => ({
  width: 8,
  height: 8,
  borderRadius: '50%',
  backgroundColor: active ? 'white' : 'rgba(255, 255, 255, 0.5)',
  cursor: 'pointer',
  transition: 'background-color 0.3s ease',
}));

// Image Slider Component
const ProjectImageSlider = ({ images }) => {
  const [currentImage, setCurrentImage] = useState(0);

  if (!images || images.length === 0) {
    return (
      <ImageSlider>
        <SliderImage
          src="/placeholder-project.jpg"
          alt="No images available"
        />
      </ImageSlider>
    );
  }


  const nextImage = () => {
    setCurrentImage((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentImage((prev) => (prev - 1 + images.length) % images.length);
  };

  const goToImage = (index) => {
    setCurrentImage(index);
  };

  return (
    <ImageSlider>
      <SliderImage
        src={resolveImageUrl(images[currentImage]?.image_url || images[currentImage])}
        alt={`Project image ${currentImage + 1}`}
      />

      {images.length > 1 && (
        <>
          <SliderButton
            onClick={prevImage}
            sx={{ left: 8 }}
          >
            <ArrowBackIos />
          </SliderButton>

          <SliderButton
            onClick={nextImage}
            sx={{ right: 8 }}
          >
            <ArrowForwardIos />
          </SliderButton>

          <SliderIndicators>
            {images.map((_, index) => (
              <SliderDot
                key={index}
                active={index === currentImage}
                onClick={() => goToImage(index)}
              />
            ))}
          </SliderIndicators>
        </>
      )}
    </ImageSlider>
  );
};

const ServiceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchService();
  }, [id]);
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, []);

  const fetchService = async () => {
    try {
      setLoading(true);
      // Fetch specific service by slug or id
      const serviceResponse = await getService(id);
      if (!serviceResponse?.data?.success || !serviceResponse?.data?.service) {
        setError('Service not found');
        return;
      }
      const foundService = serviceResponse.data.service;
      setService(foundService);
    } catch (error) {
      setError('Failed to load service details');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === '' || dateString === '0000-00-00' || dateString === null) {
      return null;
    }

    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return null;
    }

    return date.toLocaleDateString();
  };

  const getProjectStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed': return 'success';
      case 'in progress':
      case 'in-progress':
      case 'ongoing': return 'warning';
      case 'pending':
      case 'planning':
      case 'upcoming': return 'info';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getProjectStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed': return <CheckCircle />;
      case 'in progress':
      case 'in-progress':
      case 'ongoing': return <Schedule />;
      case 'pending':
      case 'planning':
      case 'upcoming': return <HourglassEmpty />;
      case 'cancelled': return <Cancel />;
      default: return <HourglassEmpty />;
    }
  };

  const getServiceIcon = (service) => {
    if (service.icon_url) {
      return (
        <img
          src={resolveImageUrl(service.icon_url)}
          alt={`${service.title} icon`}
          style={{
            width: 48,
            height: 48,
            objectFit: 'contain',
            display: 'block'
          }}
        />
      );
    }

    // Fallback to default icons if no uploaded icon
    switch (service.icon?.toLowerCase()) {
      case 'build':
      case 'construction':
        return <Build sx={{ fontSize: 48, color: 'primary.main' }} />;
      default:
        return <Star sx={{ fontSize: 48, color: 'primary.main' }} />;
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  if (error || !service) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || 'Service not found'}
        </Alert>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/services')}
        >
          Back to Services
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/services')}
          sx={{ mb: 2 }}
        >
          Back to Services
        </Button>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Box sx={{ color: 'primary.main', p: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {getServiceIcon(service)}
          </Box>
          <Box>
            <Typography
              component="h1"
              gutterBottom
              sx={{
                fontWeight: 700,
                fontSize: {
                  xs: '1.2rem',   // mobile
                  sm: '1.5rem', // tablet
                  md: '1.8rem',   // desktop
                  lg: '2.2rem', // large screens
                },
                lineHeight: 1.2,
                letterSpacing: 0.5,
              }}
            >
              {service.title}
            </Typography>
          </Box>
        </Box>
      </Box>
      <Grid container spacing={4}>
        {/* Service Details */}
        <Grid item xs={12} md={12}>
          <Box
            elevation={0}
            sx={{
              p: 4,
              mb: 3,
              borderRadius: 3,
            }}
          >
            <Typography
              variant="body1"
              paragraph
              sx={{
                fontSize: '1rem',
                color: 'text.primary',
                lineHeight: 1.7,
                letterSpacing: 0.1,
              }}
            >
              {service.description}
            </Typography>
          </Box>
        </Grid>
        {/* Features and capabilities */}
        <Grid item xs={12}>
          {service.features && service.features.length > 0 && (
            <Box
              elevation={0}
              sx={{
                p: 4,
                mb: 3,
                borderRadius: 3,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Build sx={{ fontSize: 32, color: 'secondary.main', mr: 1 }} />
                <Typography variant="h5" sx={{
                  fontWeight: 600,
                  fontSize: {
                    xs: '1.2rem',   // mobile
                    sm: '1.5rem', // tablet
                    md: '1.5rem', // desktop
                    lg: '1.8rem', // large screens
                  },
                }}
                >
                  Features cover in this service
                </Typography>
              </Box>
              <Grid container spacing={2}>
                {service.features.map((feature, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        p: 2,
                        borderRadius: 2,
                        backgroundColor: 'rgba(255,255,255,0.7)',
                        boxShadow: 1,
                      }}
                    >
                      <CheckOutlined sx={{ color: 'success.main', fontSize: 28, mr: 2 }} />
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {feature}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </Grid>

        {/* Related Projects */}
        <Grid item xs={12} width={'100%'}>
          <Typography variant="h5" gutterBottom sx={{ mt: 2, ml: 4, fontWeight: 700 }}>
            Projects linked to this service
          </Typography>
          {(!service.projects || service.projects.length === 0) ? (
            <Alert severity="info">
              No projects found for this service yet.
            </Alert>
          ) : (
            <>
              {service.projects.map((project, index) => (
                <Box
                  key={project.id}
                  sx={{
                    p: { xs: 2, sm: 3, md: 4 },
                    borderRadius: 3,
                    mb: 4,
                    maxWidth: '100%',
                    mx: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                  }}
                >
                  {/* Project Image Slider - full width */}
                  <Box sx={{ mb: 2, width: '100%' }}>
                    <ProjectImageSlider images={project.images || []} />
                  </Box>
                  <Box sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{
                        fontWeight: 700,
                        textAlign: 'left',
                        fontSize: {
                          xs: '1.2rem',
                          sm: '1.4rem',
                          md: '1.6rem',
                        },
                        mb: 1,
                      }}
                    >
                      {project.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mb: 2,
                        textAlign: 'left',
                        minHeight: '48px',
                        fontSize: {
                          xs: '1rem',
                          sm: '1.05rem',
                        },
                      }}
                    >
                      {project.description && project.description.length > 100
                        ? project.description.slice(0, 350) + '...'
                        : project.description}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      {getProjectStatusIcon(project.status)}
                      <Chip
                        label={project.status || 'Unknown'}
                        color={getProjectStatusColor(project.status)}
                        size="small"
                        variant="outlined"
                      />
                      {project.status?.toLowerCase() === 'completed' && formatDate(project.completion_date) && (
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                          Completed: {formatDate(project.completion_date)}
                        </Typography>
                      )}
                    </Box>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<Visibility />}
                      onClick={() => navigate(`/projects/${project.slug || project.id}`)}
                      sx={{
                        borderRadius: 2,
                        fontWeight: 600,
                        textTransform: 'none',
                        alignSelf: 'flex-start',
                      }}
                    >
                      View Details
                    </Button>
                  </Box>
                </Box>
              ))}
            </>
          )}
        </Grid>

      </Grid>
    </Container>
  );
};

export default ServiceDetail;
